/**
 * Utilidades de formateo reutilizables
 */

import type { SupportedLocale } from '@/i18n';
import { toIntlLocale } from '@/i18n';
import { ErrorRed, WarningOrange } from '@/constants/theme';

/**
 * Formatea una fecha a formato corto según el idioma de la app
 */
export function formatDateShort(date: Date | string, locale: SupportedLocale = 'es'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleDateString(toIntlLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Formatea una fecha a formato largo según el idioma de la app
 */
export function formatDateLong(date: Date | string, locale: SupportedLocale = 'es'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleDateString(toIntlLocale(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Información de fecha con contexto (hoy, mañana)
 */
export interface DateWithContext {
  text: string;
  badge?: string;
  badgeColor?: string;
}

/**
 * Formatea una fecha con contexto (hoy, mañana) según el idioma de la app
 */
export function formatDateWithContext(
  date: Date | string,
  locale: SupportedLocale,
  labels: { today: string; tomorrow: string }
): DateWithContext {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = typeof date === 'string' ? new Date(date) : date;
  eventDate.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const diffTime = eventDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const formattedDate = formatDateShort(eventDate, locale);

  if (diffDays === 0) {
    return {
      text: formattedDate,
      badge: labels.today,
      badgeColor: ErrorRed,
    };
  }
  if (diffDays === 1) {
    return {
      text: formattedDate,
      badge: labels.tomorrow,
      badgeColor: WarningOrange,
    };
  }

  return { text: formattedDate };
}

/**
 * Formatea una fecha a formato YYYY-MM-DD (calendario local, sin desfase UTC).
 */
export function formatDateISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Fecha de hoy en calendario local (YYYY-MM-DD). */
export function getTodayDateISO(): string {
  return formatDateISO(new Date());
}

/**
 * Parsea una fecha YYYY-MM-DD como medianoche local (evita desfases de zona horaria).
 */
export function parseDateISO(dateString: string): Date | null {
  if (!dateString) return null;

  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Genera iniciales a partir de un nombre
 */
export function generateInitials(name: string, fallback: string = 'U'): string {
  if (!name || typeof name !== 'string') {
    return fallback;
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return fallback;
  }

  const parts = trimmed.split(/\s+/);

  if (parts.length >= 2) {
    const first = parts[0].charAt(0).toUpperCase();
    const last = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${first}${last}`;
  }

  return trimmed.charAt(0).toUpperCase();
}

/**
 * Genera iniciales a partir de nombre y apellido separados
 */
export function generateInitialsFromParts(
  firstName?: string,
  lastName?: string,
  fallback: string = 'U'
): string {
  if (firstName && lastName) {
    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  }

  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }

  if (lastName) {
    return lastName.charAt(0).toUpperCase();
  }

  return fallback;
}

/**
 * Genera un nombre completo a partir de partes
 */
export function generateFullName(
  firstName?: string,
  lastName?: string,
  name?: string,
  fallback?: string
): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  if (name) {
    return name.trim();
  }

  if (firstName) {
    return firstName.trim();
  }

  return fallback || 'Usuario';
}

/**
 * Normaliza un email (trim + lowercase)
 */
export function normalizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }
  return email.trim().toLowerCase();
}

/**
 * Escapa caracteres especiales para vCard 3.0
 */
export function escapeVCardValue(value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

/**
 * Determina si un evento está "en vivo" (ocurriendo en este momento)
 */
export function isEventLive(eventDate: Date | string, eventTime?: string): boolean {
  const now = new Date();
  const eventDateObj = typeof eventDate === 'string' ? new Date(eventDate) : eventDate;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDay = new Date(eventDateObj.getFullYear(), eventDateObj.getMonth(), eventDateObj.getDate());

  if (today.getTime() !== eventDay.getTime()) {
    return false;
  }

  if (!eventTime || eventTime.trim() === '') {
    return true;
  }

  const rangeMatch = eventTime.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);

  if (rangeMatch) {
    const startHour = parseInt(rangeMatch[1], 10);
    const startMinute = parseInt(rangeMatch[2], 10);
    const endHour = parseInt(rangeMatch[3], 10);
    const endMinute = parseInt(rangeMatch[4], 10);

    const eventStartTime = new Date(
      eventDateObj.getFullYear(),
      eventDateObj.getMonth(),
      eventDateObj.getDate(),
      startHour,
      startMinute,
      0,
      0
    );

    const eventEndTime = new Date(
      eventDateObj.getFullYear(),
      eventDateObj.getMonth(),
      eventDateObj.getDate(),
      endHour,
      endMinute,
      0,
      0
    );

    const nowNormalized = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      now.getMinutes(),
      0,
      0
    );

    const liveStartTime = new Date(eventStartTime.getTime() - 5 * 60 * 1000);

    return nowNormalized >= liveStartTime && nowNormalized < eventEndTime;
  }

  const timeMatch = eventTime.match(/(\d{1,2}):(\d{2})/);
  if (!timeMatch) {
    return true;
  }

  const eventHour = parseInt(timeMatch[1], 10);
  const eventMinute = parseInt(timeMatch[2], 10);

  const eventDateTime = new Date(
    eventDateObj.getFullYear(),
    eventDateObj.getMonth(),
    eventDateObj.getDate(),
    eventHour,
    eventMinute
  );

  const liveStartTime = new Date(eventDateTime.getTime() - 5 * 60 * 1000);
  const defaultEventDuration = 1 * 60 * 60 * 1000;
  const eventEndTime = new Date(eventDateTime.getTime() + defaultEventDuration);

  return now >= liveStartTime && now < eventEndTime;
}
